import { EQUIPMENT_GROUPS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../../contexts/auth";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailSection,
} from "../../../../../components/ui/detailView";

interface EquipmentModel {
  id: string;
  display_name?: string;
  name?: string;
}

interface EquipmentGroup {
  id: string;
  name: string;
  equipment_models: EquipmentModel[];
  created_at: string;
  updated_at: string;
}

export const EquipmentGroupBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_GROUPS_ENDPOINT);
  const { setOverride, clearOverride } = useBreadcrumbs();
  const [group, setGroup] = useState<EquipmentGroup | null>(null);
  const canViewEquipmentGroups = useHasPermission("service.view_equipmentgroup");

  useEffect(() => {
    if (!id || !canViewEquipmentGroups) {
      return undefined;
    }

    const path = `/service/statistics/equipment-groups/${id}`;

    const fetchGroup = async () => {
      const response = await get(id);
      if (response.data) {
        setGroup(response.data);
        setOverride(path, response.data.name || id);
      }
    };

    const handleUpdated = () => {
      fetchGroup();
    };

    window.addEventListener("equipmentGroupUpdated", handleUpdated);
    fetchGroup();
    return () => {
      window.removeEventListener("equipmentGroupUpdated", handleUpdated);
      clearOverride(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewEquipmentGroups]);

  if (!canViewEquipmentGroups) {
    return null;
  }

  if (!group) return <DetailPage loading />;

  return (
    <DetailPage>
      <DetailHeader icon={<CategoryOutlinedIcon />} title={group.name} />

      <DetailSection title={t("Assigned Equipment Models")} padded>
        {group.equipment_models.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t("No equipment models assigned to this group yet")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {group.equipment_models.map((equipmentModel) => (
              <Chip
                key={equipmentModel.id}
                size="small"
                variant="outlined"
                label={equipmentModel.display_name || equipmentModel.name}
              />
            ))}
          </Box>
        )}
      </DetailSection>

      <DetailMetadata createdAt={group.created_at} updatedAt={group.updated_at} />
    </DetailPage>
  );
};
